import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import { canStartInterview } from '../utils/interviewEligibility';
import { getSavedJobs, unsaveJob } from '../utils/savedJobs';

export default function SavedJobsScreen() {
  const navigate = useNavigate();
  const authModal = useAuthModal();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitErrorMessage, setLimitErrorMessage] = useState('');
  const [savedJobs, setSavedJobs] = useState([]);

  useEffect(() => {
    setSavedJobs(getSavedJobs());
  }, []);

  const handleRemoveSaved = (jobId) => {
    unsaveJob(jobId);
    setSavedJobs(getSavedJobs());
  };

  const handleStartInterview = async (jobId) => {
    const check = await canStartInterview();
    if (check.unauthorized) {
      authModal.openAuth('search');
      return;
    }
    if (!check.ok) {
      setLimitErrorMessage(check.message || '');
      setShowLimitModal(true);
      return;
    }
    navigate(`/chat-setting/${jobId}`);
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8] flex flex-col">
      <SiteHeader authModal={authModal} />

      <main className="flex-grow overflow-y-auto p-6 space-y-4">
        <h1 className="text-3xl font-bold text-[#212B36] mb-6">Saved Jobs</h1>

        {savedJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-gray-600 text-lg font-medium mb-4">
              No saved jobs yet
            </p>
            <p className="text-gray-500 text-sm">
              Start searching and save jobs you're interested in
            </p>
            <button
              onClick={() => navigate('/search')}
              className="mt-6 btn-orange px-6 py-3"
            >
              Go to Search
            </button>
          </div>
        ) : (
          savedJobs.map((job) => (
            <div key={job.id} className="card space-y-3 relative">
              <button
                onClick={() => handleRemoveSaved(job.id)}
                className="absolute top-4 right-4 p-2 text-red-500 hover:bg-red-50 rounded-full transition"
              >
                <Trash2 className="w-5 h-5" />
              </button>

              <h3 className="text-xl font-bold text-gray-900 pr-10">
                {job.title}
              </h3>
              <p className="text-sm text-gray-600">
                {job.company} - {job.location}
              </p>
              <p className="text-sm font-medium text-gray-700">
                Salary: {job.salary}
              </p>
              <p className="text-gray-700 text-sm">{job.description}</p>

              <div className="flex gap-3 pt-3">
                <button
                  onClick={() => navigate(`/details/${job.id}`)}
                  className="flex-1 btn-outline-blue text-sm py-2"
                >
                  Details
                </button>
                <button
                  onClick={() => handleStartInterview(job.id)}
                  className="flex-1 btn-orange text-sm py-2"
                >
                  AI Interview
                </button>
              </div>
            </div>
          ))
        )}
      </main>

      <Footer />

      <SubscriptionLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        errorMessage={limitErrorMessage}
      />
    </div>
  );
}
